import React, { useMemo, useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useNavigate, useParams } from "react-router-dom"
import { config } from "@/config/env"
import {
  Copy, Check, Share2, Video, BarChart3, ArrowLeft, ExternalLink,
  Users, Trash2, Edit3, Calendar, Clock, Globe,
  Lock, Eye, EyeOff, ShieldCheck, X,
  CalendarClock, Radio, CheckCircle2, FileEdit,
} from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import { Card } from "@/clientWeb/components/ui/card"
import { Badge } from "@/clientWeb/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/clientWeb/components/ui/dialog"
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle } from "@/clientWeb/components/ui/drawer"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import ShareDialog from "@/features/meetings/components/meetingDashboard/ShareDialog"
import { getContactById } from "@/features/meetings/data/contacts"
import { useWebinarStore } from "./store/webinar.store"
import { webinarsService } from "./services/webinar.service"
import { useAuthStore } from "@/features/auth/store/auth.store"

export default function WebinarDetailsPage() {
  const t = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const webinarFromStore = useWebinarStore((s) => s.webinars.find((w) => w.id === id))
  const removeWebinar = useWebinarStore((s) => s.removeWebinar)
  const authUser = useAuthStore((s) => s.user)
  const [showPassword, setShowPassword] = useState(false)
  const [passwordCopied, setPasswordCopied] = useState(false)
  const [showShareDialog, setShowShareDialog] = useState(false)
  const [shareDialogUrl, setShareDialogUrl] = useState('')
  const [inviteesOpen, setInviteesOpen] = useState(false)

  // Host detection mirrors MeetingDetails. The mock service only releases the
  // plaintext when the current user actually owns the webinar.
  const isHost = useMemo(() => {
    if (!webinarFromStore || !authUser) return false
    if (webinarFromStore.hostId && authUser.id) return webinarFromStore.hostId === authUser.id
    if (webinarFromStore.hostEmail && authUser.email)
      return webinarFromStore.hostEmail === authUser.email
    return false
  }, [webinarFromStore, authUser])

  const webinarPassword = useMemo(() => {
    if (!webinarFromStore?.passwordEnabled || !isHost) return undefined
    return webinarsService.getPasswordForHost(
      webinarFromStore.id,
      authUser?.id,
      authUser?.email,
    )
  }, [webinarFromStore, isHost, authUser?.id, authUser?.email])

  const handleCopyPassword = async () => {
    if (!webinarPassword) return
    await navigator.clipboard.writeText(webinarPassword)
    setPasswordCopied(true)
    toast({
      title: t('meetings.passwordCopied'),
      description: t('meetings.passwordCopiedDesc'),
      variant: 'success',
      duration: 2000,
    })
    setTimeout(() => setPasswordCopied(false), 1600)
  }

  if (!webinarFromStore) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
        <Video className="w-12 h-12 text-muted-foreground opacity-40" />
        <p className="text-muted-foreground">{t('webinar.webinarNotFound')}</p>
        <Button variant="outline" onClick={() => navigate('/webinar')}>
          <ArrowLeft className="w-4 h-4 mr-2" />{t('common.back')}
        </Button>
      </div>
    )
  }

  const endTimeStr = (() => {
    const e = new Date(webinarFromStore.endTimeMs)
    return `${e.getHours().toString().padStart(2,'0')}:${e.getMinutes().toString().padStart(2,'0')}`
  })()

  const webinar = {
    id: webinarFromStore.id,
    title: webinarFromStore.title,
    description: webinarFromStore.description,
    startTime: webinarFromStore.time,
    endTime: endTimeStr,
    duration: webinarFromStore.duration,
    status: webinarFromStore.status,
    host: { name: webinarFromStore.hostName, email: webinarFromStore.hostEmail },
    maxRegistrants: webinarFromStore.maxRegistrants,
    password: webinarPassword,
    webinarLink: `${config.webinarBaseUrl}/join?id=${webinarFromStore.id}`,
  }

  const isCompleted = webinar.status === "completed"

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text)
    setCopiedField(field)
    toast({ title: t("webinar.copiedToClipboard"), description: t("webinar.fieldCopied"), variant: "success", duration: 2000 })
    setTimeout(() => setCopiedField(null), 2000)
  }

  const handleDelete = () => {
    setShowDeleteConfirm(false)
    if (webinarFromStore) removeWebinar(webinarFromStore.id)
    toast({ title: t("webinar.webinarDeleted"), description: t("webinar.deletedSuccessfully"), variant: "success", duration: 2000 })
    navigate("/webinar")
  }

  const InfoRow = ({
    label, value, icon: Icon
  }: { label: string; value: React.ReactNode; icon?: any }) => (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-6 py-4 border-b border-stroke-100 dark:border-border dark:border-slate-800/50 last:border-0 group">
      <div className="flex items-center gap-2 w-full sm:w-48 shrink-0">
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">{label}</span>
      </div>
      <div className="text-sm text-slate-800 dark:text-slate-200 font-medium flex-1">{value}</div>
    </div>
  )

  const UrlRow = ({ label, url }: { label: string; url: string }) => (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 py-4 border-b border-stroke-100 dark:border-border dark:border-slate-800/50 last:border-0 group">
      <div className="flex items-center gap-2 w-full sm:w-48 shrink-0">
        <ExternalLink className="w-4 h-4 text-slate-400" />
        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">{label}</span>
      </div>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <span className="text-sm text-primary font-semibold truncate hover:underline cursor-pointer">{url}</span>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleCopy(url, label)}>
            {copiedField === label ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8"
            onClick={() => { setShareDialogUrl(url); setShowShareDialog(true) }}>
            <Share2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  )

  const statusConfig: Record<string, { icon: React.ReactNode; label: string; className: string }> = {
    upcoming: {
      icon: <CalendarClock className="w-3 h-3" />,
      label: t("webinar.upcoming"),
      className: "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-semibold shadow-md shadow-blue-500/20",
    },
    ongoing: {
      icon: <Radio className="w-3 h-3" />,
      label: t("webinar.live"),
      className: "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 text-white text-xs font-semibold shadow-md shadow-emerald-500/20 animate-pulse",
    },
    completed: {
      icon: <CheckCircle2 className="w-3 h-3" />,
      label: t("webinar.completed"),
      className: "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-slate-500 to-slate-600 text-white text-xs font-semibold shadow-md shadow-slate-500/20",
    },
    draft: {
      icon: <FileEdit className="w-3 h-3" />,
      label: t("webinar.draft"),
      className: "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-semibold shadow-md shadow-amber-500/20",
    },
  }
  const getStatusBadge = (status: string) => {
    const cfg = statusConfig[status] ?? {
      icon: null,
      label: status,
      className: "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold",
    }
    return <div className={cfg.className}>{cfg.icon}{cfg.label}</div>
  }

  return (
    <div className="h-full w-full overflow-y-auto bg-background p-4 md:p-8">
      <div className="max-w-5xl mx-auto space-y-5 pb-10">

        {/* Breadcrumb */}
        {/* <div className="flex items-center gap-2 text-xs text-slate-400">
          <button onClick={() => navigate("/webinar")} className="hover:text-primary transition-colors">Webinar</button>
          <span>/</span>
          <span className="text-slate-700 dark:text-slate-200 font-medium">Webinar Details</span>
        </div> */}

        {/* Top Nav */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline" size="icon"
              onClick={() => navigate("/webinar")}
              className="rounded-full h-10 w-10 border-stroke-100 dark:border-border dark:border-slate-800 shadow-sm hover:border-primary hover:text-primary"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{webinar.title}</h1>
                {getStatusBadge(webinar.status)}
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t("webinar.webinarDetails")}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {!isCompleted && (
              <Button
                variant="outline" size="sm"
                onClick={() => { setShareDialogUrl(webinar.webinarLink); setShowShareDialog(true) }}
                title={t("common.share")}
              >
                <Share2 className="w-4 h-4 mr-1.5" /> {t("common.share")}
              </Button>
            )}
            <Button
              variant="outline" size="sm"
              onClick={() => setShowDeleteConfirm(true)}
              className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900/50 dark:text-red-400 dark:hover:bg-red-500/10"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> {t("common.delete")}
            </Button>
            {!isCompleted && (
              <Button variant="outline" size="sm" onClick={() => navigate(`/webinar/create?edit=${webinar.id}`)}>
                <Edit3 className="w-4 h-4 mr-1.5" /> {t("common.edit")}
              </Button>
            )}
            {isCompleted && (
              <>
                <Button
                  variant="outline" size="sm"
                  onClick={() => navigate(`/webinar/${webinar.id}/past`)}
                  className="gap-1.5"
                >
                  <Video className="w-4 h-4" /> {t("webinar.pastDetails")}
                </Button>
                <Button
                  size="sm"
                  onClick={() => navigate(`/webinar/${webinar.id}/analytics`)}
                  className="gap-1.5"
                >
                  <BarChart3 className="w-4 h-4" /> {t("webinar.viewAnalytics")}
                </Button>
              </>
            )}
            {!isCompleted && (
              <Button
                size="sm"
                onClick={() => {
                  const name = authUser?.name || "Guest"
                  navigate(`/meeting/${webinar.id}?name=${encodeURIComponent(name)}`)
                }}
              >
                <Video className="w-4 h-4 mr-1.5" />
                {webinar.status === "ongoing" ? t("webinar.joinWebinar") : t("webinar.startWebinar")}
              </Button>
            )}
          </div>
        </div>

        {/* Detail Card */}
        <Card className="bg-card dark:bg-card/60 border-stroke-100 dark:border-border/70 dark:border-slate-800/60 shadow-sm rounded-2xl overflow-hidden">
          <div className="p-6 divide-y divide-slate-50 dark:divide-slate-800/30">
            <InfoRow label={t("webinar.webinarName")} value={webinar.title} icon={Video} />
            <InfoRow
              label={t("common.description")}
              value={
                <p className="text-slate-600 dark:text-slate-400 font-normal leading-relaxed">
                  {webinar.description}
                </p>
              }
            />
            <InfoRow label={t("webinar.createdBy")} value={webinar.host.name} icon={Users} />
            <InfoRow label={t("webinar.webinarId")} value={<code className="bg-slate-100 dark:bg-card px-2 py-0.5 rounded text-primary font-mono text-xs">{webinar.id}</code>} />
            <InfoRow
              label={t("webinar.eventDateTime")}
              value={`${new Date(webinarFromStore.startTimeMs).toLocaleDateString('en-GB')}, ${webinar.startTime}–${webinar.endTime}`}
              icon={Calendar}
            />
            <InfoRow label={t("common.duration")} value={`${webinar.duration} ${t("common.minutes")}`} icon={Clock} />
            <InfoRow label={t("webinar.timeZone")} value={webinarFromStore.timezone ?? "Asia/Calcutta"} icon={Globe} />
            {webinarFromStore.additionalTimezones.length > 0 && (
              <InfoRow
                label={t("webinar.additionalTimezones")}
                value={
                  <div className="flex flex-wrap gap-1.5">
                    {webinarFromStore.additionalTimezones.map((tz) => (
                      <Badge key={tz} variant="secondary" className="text-[11px] gap-1">
                        <Globe className="w-3 h-3" /> {tz}
                      </Badge>
                    ))}
                  </div>
                }
              />
            )}
            <InfoRow label={t("webinar.maxRegistrants")} value={webinar.maxRegistrants} icon={Users} />
            {webinarFromStore.security.allowJoinAnytime && (
              <InfoRow label={t("webinar.anyTimeJoin")} value={<Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20"><Check className="w-3 h-3 mr-1" />{t("common.enabled")}</Badge>} icon={Clock} />
            )}
            {webinarFromStore.security.recording && (
              <InfoRow label={t("webinar.recording")} value={<Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20"><Check className="w-3 h-3 mr-1" />{t("common.enabled")}</Badge>} icon={Video} />
            )}
            <InfoRow
              label={t("webinar.invitees")}
              value={
                webinarFromStore.invitees.length === 0 ? (
                  <span className="text-xs text-muted-foreground">{t("webinar.noInvitees")}</span>
                ) : (
                  <div
                    className="flex -space-x-2 cursor-pointer"
                    onClick={() => setInviteesOpen(true)}
                    title={t("webinar.invitees")}
                  >
                    {webinarFromStore.invitees.slice(0, 6).map((id, i) => {
                      const contact = getContactById(id)
                      const displayName = contact?.name ?? id
                      const colors = ['#F4D5DB','#D5E3F4','#D5F4D5','#E3D5F4','#F4E3D5']
                      const bg = colors[displayName.charCodeAt(0) % colors.length]
                      return (
                        <div
                          key={i}
                          className="h-7 w-7 rounded-full border-2 border-card flex items-center justify-center text-[10px] font-bold text-slate-700"
                          style={{ backgroundColor: bg }}
                          title={displayName}
                        >
                          {displayName.charAt(0).toUpperCase()}
                        </div>
                      )
                    })}
                    {webinarFromStore.invitees.length > 6 && (
                      <div className="h-7 w-7 rounded-full border-2 border-card bg-muted flex items-center justify-center">
                        <span className="text-[10px] font-bold text-muted-foreground">+{webinarFromStore.invitees.length - 6}</span>
                      </div>
                    )}
                  </div>
                )
              }
            />
          </div>
        </Card>

        {/* Security & advanced — host sees the generated password, everyone
            sees the active toggles. Mirrors the MeetingDetails treatment. */}
        {webinarFromStore.passwordEnabled && !isCompleted && (
          <Card className="bg-card dark:bg-card/60 border-stroke-100 dark:border-border/70 dark:border-slate-800/60 shadow-sm rounded-2xl overflow-hidden">
            <div className="px-6 pt-5 pb-4 space-y-4">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">
                  {t('meetings.securityAdvanced')}
                </h3>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                  <Lock className="w-4 h-4 text-primary dark:text-foreground" />
                  <span className="text-sm font-medium">{t('meetings.meetingPassword')}</span>
                </div>
                {isHost && webinarPassword ? (
                  <div className="flex items-center gap-2 bg-card dark:bg-background/40 border border-stroke-100 dark:border-border rounded-lg px-3 py-2">
                    <code className="flex-1 text-sm font-mono tracking-widest text-slate-800 dark:text-slate-100 truncate">
                      {showPassword ? webinarPassword : '•'.repeat(webinarPassword.length)}
                    </code>
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="text-slate-500 dark:text-slate-400 hover:text-primary transition-colors p-1"
                      title={showPassword ? t('meetings.hidePassword') : t('meetings.showPassword')}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      type="button"
                      onClick={handleCopyPassword}
                      className="text-slate-500 dark:text-slate-400 hover:text-primary transition-colors p-1 inline-flex items-center gap-1"
                      title={passwordCopied ? t('common.copied') : t('meetings.copyPassword')}
                    >
                      {passwordCopied ? (
                        <Check className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {t('meetings.participantsPrompted')}
                  </p>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* URL Links */}
        <Card className="bg-card dark:bg-card/60 border-stroke-100 dark:border-border/70 dark:border-slate-800/60 shadow-sm rounded-2xl overflow-hidden">
          <div className="px-6 pt-5 pb-1">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.linksAndUrls")}</h3>
          </div>
          <div className="px-6 pb-4 divide-y divide-slate-50 dark:divide-slate-800/30">
            <UrlRow label={t("webinar.registrationUrl")} url={`${config.webinarBaseUrl}/register?id=${webinar.id}&sessionId=${webinar.id}&reg=true`} />
            <UrlRow label={t("webinar.webinarUrl")} url={`${config.webinarBaseUrl}/join?id=${webinar.id}&sessionId=${webinar.id}`} />
            <UrlRow label={t("webinar.speakerUrl")} url={`${config.appBaseUrl}/speaker/${webinar.id}`} />
          </div>
        </Card>

      </div>

      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('webinar.deleteWebinar')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('webinar.deleteWebinarWarning')}
          </p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={handleDelete}>{t('common.delete')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Dialog — reuses the same ShareDialog as MeetingDetails */}
      <ShareDialog
        open={showShareDialog}
        onOpenChange={setShowShareDialog}
        meetingUrl={shareDialogUrl}
        sipAddress={shareDialogUrl}
      />

      {/* Invitees Drawer */}
      <Drawer open={inviteesOpen} onOpenChange={setInviteesOpen}>
        <DrawerContent direction="right" width="w-full sm:w-[400px]">
          <DrawerHeader className="px-5 py-3.5 border-b border-border flex flex-row items-center justify-between shrink-0">
            <DrawerTitle className="text-base font-semibold">
              {t('webinar.invitees')} ({webinarFromStore.invitees.length})
            </DrawerTitle>
            <DrawerClose asChild>
              <button className="w-8 h-8 rounded-full flex items-center justify-center text-primary border border-primary hover:bg-primary/10 transition-all">
                <X className="w-4 h-4" />
              </button>
            </DrawerClose>
          </DrawerHeader>
          <div className="flex-1 overflow-y-auto px-5 py-4">
            <div className="space-y-2">
              {webinarFromStore.invitees.map((id, i) => {
                const contact = getContactById(id)
                const displayName = contact?.name ?? id
                const displayEmail = contact?.email ?? ''
                const colors = ['#F4D5DB','#D5E3F4','#D5F4D5','#E3D5F4','#F4E3D5']
                const bg = colors[displayName.charCodeAt(0) % colors.length]
                return (
                  <div key={i} className="flex items-center gap-3 rounded-xl border border-border/50 px-3 py-2.5 bg-muted/20">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold text-slate-700 shrink-0"
                      style={{ backgroundColor: bg }}
                    >
                      {displayName.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
                      {displayEmail && <p className="text-xs text-muted-foreground truncate">{displayEmail}</p>}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  )
}
